import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  Users,
  Clock,
  CalendarOff,
  Inbox,
  Timer,
  LayoutDashboard,
  Bell,
  AlertTriangle,
  Link2,
  Unlink,
  Settings2,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtFecha, fmtHora, fmtHoras } from "@/lib/format";
import { TIPO_FICHAJE } from "@/lib/fichajeUi";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import PageHeader from "@/components/PageHeader";
import CumplimientoFichaje from "@/components/CumplimientoFichaje";
import PanelConfigDialog from "@/components/PanelConfigDialog";
import EstadoVivoPanel from "@/components/EstadoVivoPanel";
import GraficoPausasPanel from "@/components/GraficoPausasPanel";
import { sectionGradientStyle, kpiSectionKey } from "@/lib/sectionTheme";
import { useAuth } from "@/context/AuthContext";

const KPI_DEFS = {
  kpi_empleados: { icon: Users, label: "Empleados", styleKey: "empleados", testid: "stat-empleados", getValue: (s) => s.total_empleados },
  kpi_trabajando: { icon: Clock, label: "Trabajando", styleKey: "trabajando", testid: "stat-trabajando", getValue: (s) => s.trabajando_ahora },
  kpi_ausentes: { icon: CalendarOff, label: "Ausentes hoy", styleKey: "ausentes", testid: "stat-ausentes", getValue: (s) => s.ausentes_hoy },
  kpi_horas: { icon: Timer, label: "Horas hoy", styleKey: "horas", testid: "stat-horas", getValue: (s) => fmtHoras(s.segundos_hoy) },
  kpi_pendientes: { icon: Inbox, label: "Pendientes", styleKey: "pendientes", testid: "stat-pendientes", getValue: (s) => s.solicitudes_pendientes },
};

function StatCard({ icon: Icon, label, value, testid, styleKey }) {
  return (
    <div
      className="rounded-xl p-5 text-white shadow-md transition-shadow hover:shadow-lg"
      style={sectionGradientStyle(kpiSectionKey(styleKey), "to bottom right")}
    >
      <div className="flex items-center gap-2 text-white/80">
        <Icon className="h-4 w-4" strokeWidth={1.5} />
        <p className="text-[11px] font-semibold uppercase tracking-[0.1em]">{label}</p>
      </div>
      <p className="font-display mt-2 text-3xl font-light tracking-tight" data-testid={testid}>
        {value}
      </p>
    </div>
  );
}

function PendientesCard({ value }) {
  const Icon = KPI_DEFS.kpi_pendientes.icon;
  const activo = value > 0;
  return (
    <Link to="/admin/solicitudes" className="block">
      <div
        className={`h-full rounded-xl p-5 shadow-md transition-shadow hover:shadow-lg ${activo ? "text-white" : "border border-zinc-200 bg-white"}`}
        style={activo ? sectionGradientStyle(kpiSectionKey("pendientes"), "to bottom right") : undefined}
      >
        <div className={`flex items-center gap-2 ${activo ? "text-white/80" : "text-zinc-400"}`}>
          <Icon className="h-4 w-4" strokeWidth={1.5} />
          <p className="text-[11px] font-semibold uppercase tracking-[0.1em]">Pendientes</p>
        </div>
        <p className={`font-display mt-2 text-3xl font-light tracking-tight ${activo ? "text-white" : ""}`} data-testid="stat-pendientes">
          {value}
        </p>
      </div>
    </Link>
  );
}

function mensajeRecordatorioFichaje(f) {
  if (f.falta_pareja && f.pareja_tipo === "sin_salida") {
    return `Recuerda fichar la salida de tu jornada (${fmtFecha(f.timestamp)} · entrada registrada a las ${fmtHora(f.timestamp)}).`;
  }
  if (f.falta_pareja && f.pareja_tipo === "sin_entrada") {
    return `Revisa tu fichaje de salida del ${fmtFecha(f.timestamp)}: no consta la entrada correspondiente.`;
  }
  if (f.tramo_horario) {
    return `Revisa tu fichaje del tramo ${f.tramo_horario} (${fmtFecha(f.timestamp)}).`;
  }
  return "Recuerda completar tu registro de jornada (entrada y salida de cada tramo).";
}

function UltimosFichajes({ fichajes, avisandoId, onAvisar }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm">
      <div className="border-b border-indigo-100 bg-gradient-to-r from-indigo-50 to-violet-50 px-5 py-4">
        <h2 className="font-display text-lg font-medium text-indigo-900">Últimos fichajes</h2>
        <p className="mt-1 text-xs text-indigo-700/80">Retrasos, parejas entrada/salida e incidencias de picado incompleto</p>
      </div>
      {fichajes.length === 0 ? (
        <p className="p-6 text-center text-sm text-zinc-400">Sin actividad reciente.</p>
      ) : (
        <table className="w-full text-sm" data-testid="ultimos-fichajes-tabla">
          <thead>
            <tr className="border-b border-zinc-100 text-left text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
              <th className="px-5 py-2">Empleado</th>
              <th className="px-5 py-2 hidden sm:table-cell">Departamento</th>
              <th className="px-5 py-2">Fichaje</th>
              <th className="px-5 py-2">Estado</th>
              <th className="px-5 py-2 text-right">Fecha / hora</th>
              <th className="px-5 py-2 text-right w-24">Aviso</th>
            </tr>
          </thead>
          <tbody>
            {fichajes.map((f) => {
              const tipoMeta = TIPO_FICHAJE[f.tipo];
              const tipoLabel = tipoMeta?.label || f.tipo;
              const necesitaAviso = f.falta_pareja || f.con_retraso || f.es_autocierre;
              return (
                <tr
                  key={f.id}
                  className={`border-b border-zinc-100 last:border-0 hover:bg-zinc-50 ${f.falta_pareja ? "bg-amber-50/40" : ""}`}
                >
                  <td className="px-5 py-2.5 font-medium">{f.usuario}</td>
                  <td className="px-5 py-2.5 text-zinc-500 hidden sm:table-cell">{f.departamento || "—"}</td>
                  <td className="px-5 py-2.5">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Badge
                        variant="outline"
                        className={
                          tipoMeta?.className ||
                          (f.tipo === "entrada" || f.tipo === "vuelta"
                            ? "border-green-200 bg-green-100 text-green-700"
                            : f.tipo === "pausa"
                              ? "border-amber-200 bg-amber-100 text-amber-800"
                              : "border-zinc-200 bg-zinc-100 text-zinc-600")
                        }
                      >
                        {tipoLabel}
                      </Badge>
                      {f.tramo_horario && <span className="text-[10px] text-zinc-400">{f.tramo_horario}</span>}
                    </div>
                  </td>
                  <td className="px-5 py-2.5">
                    <div className="flex flex-wrap gap-1">
                      {f.con_retraso && (
                        <Badge variant="outline" className="border-orange-200 bg-orange-50 text-orange-800 gap-0.5">
                          <AlertTriangle className="h-3 w-3" />
                          Retraso{f.retraso_minutos != null ? ` +${f.retraso_minutos}m` : ""}
                        </Badge>
                      )}
                      {f.retraso_justificado && !f.con_retraso && (
                        <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-800 gap-0.5">
                          Retraso justificado
                          {f.retraso_minutos != null ? ` (+${f.retraso_minutos}m)` : ""}
                        </Badge>
                      )}
                      {f.sesion_completa && (
                        <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-800 gap-0.5">
                          <Link2 className="h-3 w-3" />
                          Pareja OK
                        </Badge>
                      )}
                      {f.falta_pareja && f.pareja_tipo === "salida_automatica" && (
                        <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-800 gap-0.5">
                          <Unlink className="h-3 w-3" />
                          Salida automática
                        </Badge>
                      )}
                      {f.falta_pareja && f.pareja_tipo === "sin_salida" && (
                        <Badge variant="outline" className="border-rose-200 bg-rose-50 text-rose-800 gap-0.5">
                          <Unlink className="h-3 w-3" />
                          Sin salida
                        </Badge>
                      )}
                      {f.falta_pareja && f.pareja_tipo === "sin_entrada" && (
                        <Badge variant="outline" className="border-rose-200 bg-rose-50 text-rose-800 gap-0.5">
                          <Unlink className="h-3 w-3" />
                          Sin entrada
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-2.5 text-right text-zinc-500 whitespace-nowrap">
                    {fmtFecha(f.timestamp)} · {fmtHora(f.timestamp)}
                  </td>
                  <td className="px-5 py-2.5 text-right">
                    {necesitaAviso && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="h-8 gap-1 text-amber-700"
                        disabled={avisandoId === f.id}
                        onClick={() => onAvisar(f)}
                        data-testid={`avisar-fichaje-${f.id}`}
                      >
                        <Bell className="h-3.5 w-3.5" />
                        <span className="hidden lg:inline">Avisar</span>
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const soloGerencia =
    user?.rol === "worker" && (user?.es_aprobador_gerencia || user?.es_miembro_gerencia);

  const [stats, setStats] = useState(null);
  const [estadoVivo, setEstadoVivo] = useState(null);
  const [pausasHoy, setPausasHoy] = useState(null);
  const [panelConfig, setPanelConfig] = useState(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [avisandoId, setAvisandoId] = useState(null);

  const visibleSet = useMemo(() => new Set(panelConfig?.visible || []), [panelConfig]);
  const orden = panelConfig?.orden || [];

  const necesitaStats = useMemo(() => {
    if (!panelConfig) return true;
    return orden.some(
      (id) => visibleSet.has(id) && (id.startsWith("kpi_") || id === "ultimos_fichajes")
    );
  }, [panelConfig, orden, visibleSet]);

  const necesitaEstadoVivo = useMemo(
    () => panelConfig && orden.some((id) => visibleSet.has(id) && id === "estado_vivo"),
    [panelConfig, orden, visibleSet]
  );

  const necesitaPausas = useMemo(
    () => panelConfig && orden.some((id) => visibleSet.has(id) && id === "grafico_pausas"),
    [panelConfig, orden, visibleSet]
  );

  const cargarConfig = useCallback(async () => {
    try {
      const { data } = await api.get("/dashboard/panel-config");
      setPanelConfig(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  const cargarStats = useCallback(async () => {
    if (!necesitaStats) {
      setStats({ ultimos_fichajes: [] });
      return;
    }
    try {
      const { data } = await api.get("/dashboard/stats");
      setStats(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [necesitaStats]);

  const cargarTiempoReal = useCallback(async () => {
    try {
      if (necesitaEstadoVivo) {
        const { data } = await api.get("/dashboard/estado-vivo");
        setEstadoVivo(data);
      } else {
        setEstadoVivo(null);
      }
      if (necesitaPausas) {
        const { data } = await api.get("/dashboard/pausas-hoy");
        setPausasHoy(data);
      } else {
        setPausasHoy(null);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [necesitaEstadoVivo, necesitaPausas]);

  useEffect(() => {
    if (soloGerencia) return;
    cargarConfig();
  }, [cargarConfig, soloGerencia]);

  useEffect(() => {
    if (soloGerencia || !panelConfig) return;
    cargarStats();
    cargarTiempoReal();
    const needsPoll = necesitaStats || necesitaEstadoVivo || necesitaPausas;
    if (!needsPoll) return undefined;
    const t = setInterval(() => {
      cargarStats();
      cargarTiempoReal();
    }, 60000);
    return () => clearInterval(t);
  }, [panelConfig, cargarStats, cargarTiempoReal, necesitaStats, necesitaEstadoVivo, necesitaPausas, soloGerencia]);

  const avisarTrabajador = async (f) => {
    setAvisandoId(f.id);
    try {
      await api.post("/fichajes/recordatorios", {
        usuario_ids: [f.usuario_id],
        mensaje: mensajeRecordatorioFichaje(f),
        enviar_email: true,
      });
      toast.success(`Aviso enviado a ${f.usuario}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAvisandoId(null);
    }
  };

  const kpisVisibles = orden.filter((id) => visibleSet.has(id) && id.startsWith("kpi_"));
  const bloquesVisibles = orden.filter((id) => visibleSet.has(id) && !id.startsWith("kpi_"));

  if (soloGerencia) {
    return <Navigate to="/admin/vacaciones" replace />;
  }

  if (!panelConfig || (necesitaStats && !stats) || (necesitaEstadoVivo && !estadoVivo) || (necesitaPausas && !pausasHoy)) {
    return <div className="h-8 w-8 animate-spin rounded-full border-2 border-zinc-200 border-t-zinc-950" />;
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <PageHeader
        icon={LayoutDashboard}
        title="Panel"
        subtitle="Resumen de la actividad de hoy"
        sectionKey="panel"
        brandGradient={false}
      >
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="gap-1.5 bg-white/15 text-white hover:bg-white/25 border-0"
          onClick={() => setConfigOpen(true)}
          data-testid="panel-personalizar-btn"
        >
          <Settings2 className="h-4 w-4" />
          Personalizar
        </Button>
      </PageHeader>

      {visibleSet.size === 0 ? (
        <div className="rounded-xl border border-dashed border-zinc-300 bg-zinc-50 p-8 text-center">
          <p className="text-sm text-zinc-600">No tienes ningún bloque visible en el panel.</p>
          <Button type="button" variant="outline" size="sm" className="mt-3" onClick={() => setConfigOpen(true)}>
            Configurar panel
          </Button>
        </div>
      ) : (
        <>
          {kpisVisibles.length > 0 && stats && (
            <div
              className={`grid gap-4 ${
                kpisVisibles.length === 1
                  ? "grid-cols-1"
                  : kpisVisibles.length === 2
                    ? "grid-cols-2"
                    : kpisVisibles.length === 3
                      ? "grid-cols-2 lg:grid-cols-3"
                      : kpisVisibles.length === 4
                        ? "grid-cols-2 lg:grid-cols-4"
                        : "grid-cols-2 lg:grid-cols-5"
              }`}
            >
              {kpisVisibles.map((id) => {
                const def = KPI_DEFS[id];
                if (!def) return null;
                if (id === "kpi_pendientes") {
                  return <PendientesCard key={id} value={def.getValue(stats)} />;
                }
                return (
                  <StatCard
                    key={id}
                    icon={def.icon}
                    label={def.label}
                    value={def.getValue(stats)}
                    testid={def.testid}
                    styleKey={def.styleKey}
                  />
                );
              })}
            </div>
          )}

          {bloquesVisibles.map((id) => {
            if (id === "estado_vivo") {
              return <EstadoVivoPanel key={id} data={estadoVivo} />;
            }
            if (id === "grafico_pausas") {
              return <GraficoPausasPanel key={id} data={pausasHoy} />;
            }
            if (id === "cumplimiento") {
              return <CumplimientoFichaje key={id} />;
            }
            if (id === "ultimos_fichajes" && stats) {
              return (
                <UltimosFichajes
                  key={id}
                  fichajes={stats.ultimos_fichajes}
                  avisandoId={avisandoId}
                  onAvisar={avisarTrabajador}
                />
              );
            }
            return null;
          })}
        </>
      )}

      <PanelConfigDialog
        open={configOpen}
        onClose={() => setConfigOpen(false)}
        onSaved={(data) => {
          setPanelConfig(data);
          setStats(null);
          setEstadoVivo(null);
          setPausasHoy(null);
        }}
      />
    </div>
  );
}
